import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { openWindow, redirectPage } from 'src/app/actions/core.actions';
import * as BookmarkProblemListActions from 'src/app/actions/bookmark-problem-list.actions';
import * as BookmarkProblemsActions from 'src/app/actions/bookmark-problems.actions';
import { BookmarkProblem } from 'src/app/models/bookmark';
import { ReadableNationalLanguagePlaylistProblem, ReadableNationalLanguageProblem } from 'src/app/models/problem';
import { RootState } from 'src/app/reducers';
import { Log } from 'src/app/utils/log';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { AppEvent } from '../../../models/event-log';
import { reportAppEvent } from '../../../actions/event-log.actions';
import { WordFileUtil } from '../../../utils/word-file-util';
import { RoutingPathResolver } from 'src/app/app-routing-path-resolver';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatTableDataSource } from '@angular/material/table';
import { BookmarkProblemListWithCheck } from '../../widgets/button-toggle-bookmark-with-popover/button-toggle-bookmark-with-popover.component';
import { User } from '../../../models/user';
import {
  BookmarkProblemListTableData,
  FindMyBookmarkProblemListParams,
  FindSharedBookmarkProblemListParams
} from '../../../models/bookmark-problem-list';
import { BookmarkProblemsCollection, FindBookmarkProblemsParams } from '../../../models/bookmark-problem';

import { getSignedInUser } from 'src/app/selectors/auth.selectors';
import * as BookmarkSelectors from 'src/app/selectors/bookmark.selectors';
import * as BookmarkActions from 'src/app/actions/bookmark.actions';
import * as BookmarkProblemsSelectors from 'src/app/selectors/bookmark-problems.selectors';
import * as BookmarkProblemListSelectors from 'src/app/selectors/bookmark-problem-list.selectors';
import { BookmarkProblemListsUtil } from '../../../utils/bookmark-problem-lists-util';
import {
  DIALOG_ZERO_PADDING_PANEL_CLASS,
  UNCATEGORIZED_BOOKMARK_PROBLEM_LISTS_DEFAULT_MAX_COUNT,
  WORD_DOWNLOAD_ALERT_DIALOG_WIDTH
} from '../../../resources/config';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { WordDownloadAlertDialogComponent } from '../word-download-alert-dialog/word-download-alert-dialog.component';
import { TabVisibilityService } from 'src/app/services/tab-visibility.service';

interface Description {
  field: string;
  problemOrder: string;
  genre: string;
  author: string;
  title: string;
  titleComplement: string;
  characterCount: string;
  overview: string;
}

interface ProblemData {
  sequentialId: string;
  id: string;
  university: string;
  departments: string;
  year: number;
  outline: {
    contents: string;
    descriptions: Description[];
    isCommonOverview: boolean;
    highlight?: string;
  };
  level: string;
  book: string;
  page: number;
  problemNumber: string;

  comment?: string;
  pdfPath?: string;
  pdfDownloading?: boolean;

  hasExternalData: boolean;
  hasWordData: boolean;
  isBookmarked?: boolean;

  bookmarkProblemLists?: BookmarkProblemListWithCheck[];
}

type ProblemId = string;

@Component({
  selector: 'app-national-language-problems',
  templateUrl: './national-language-problems.component.html',
  styleUrls: ['./national-language-problems.component.scss']
})
export class NationalLanguageProblemsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() readableNationalLanguageProblems: ReadableNationalLanguageProblem[];
  @Input() readableNationalLanguagePlaylistProblems: ReadableNationalLanguagePlaylistProblem[];
  @Input() keywords: string[];
  @Input() problemButtonDisabled = false;
  @Input() wordButtonDisabled = false;
  @Input() hideLastBottomBorder = false;
  @Input() bookmarkedAllFlag: boolean;
  @Input() isTrial: boolean;
  @Input() playListId: string;
  @Input() themeId: string;
  @Input() isDraggable: boolean;
  @Input() isForPrint = false;
  @Input() isShowBookmark = true;
  @Input() disabled: boolean;
  @Input() bookmarkProblemListId: string;
  @Input() subjectId: string;

  @Output() problemClick = new EventEmitter<{ problemId: ProblemId; year: number }>();
  @Output() dropped = new EventEmitter<{ bookmarkProblemListId: string; problemIds: string[] }>();
  @Output() initialized = new EventEmitter<void>();
  @Output() totalProblemCount = new EventEmitter<{ problemCount: number }>();

  columns: string[];
  dataSource = new MatTableDataSource<ProblemData>([]);
  isDragging = false;
  bookmarkProblemLists: BookmarkProblemListWithCheck[] = [];
  signedInUser$: Observable<User>;
  nationalLanguageBookmarkProblemLists$: Observable<BookmarkProblemListTableData[]>;
  nationalLanguageBookmarkProblems$: Observable<BookmarkProblemsCollection[]>;
  nationalLanguageUncategorizedBookmarkProblems$: Observable<BookmarkProblem[]>;
  uncategorizedBookmarkProblemCount$: Observable<number>;
  problemMaxCount: number;

  private LOG_SOURCE = this.constructor.name;
  private objectUrls: string[] = [];
  private highlightPipe: HighlightPipe;
  private visibilitySub?: Subscription;

  constructor(private store: Store<RootState>, private dialog: MatDialog, private tabVisibility: TabVisibilityService) {
    this.highlightPipe = new HighlightPipe();
  }

  ngOnInit() {
    // 検索結果一覧印刷画面では何もしない
    if (this.isForPrint) return;

    // 検索結果や問題詳細のタブが再度開かれた場合は、データを取得し直してテーブルにデータを設定(別タブで更新があった場合に反映するため)
    this.visibilitySub = this.tabVisibility.onVisibilityChange().subscribe(state => {
      if (state === 'visible') {
        Log.debug(this.LOG_SOURCE, '国語の問題コンポーネントのあるタブが再度開かれました ', state);
        this.setUpBookmarkProblemLists();
        this.setUpBookmarkProblems();
        this.ngOnChanges();
      }
      return;
    });
  }

  ngOnChanges() {
    if (!this.readableNationalLanguageProblems && !this.readableNationalLanguagePlaylistProblems) return;
    const dataCount = this.readableNationalLanguageProblems
      ? this.readableNationalLanguageProblems.length
      : this.readableNationalLanguagePlaylistProblems
      ? this.readableNationalLanguagePlaylistProblems.length
      : 'none';
    Log.debug(this.LOG_SOURCE, `input data length: ${dataCount}`);

    // 国語は問題IDの重複があるため、重複を削除した問題の件数を親コンポーネントに渡す
    if (dataCount !== undefined) {
      let problemCount = 0;
      if (dataCount !== 'none' && dataCount !== 0) {
        problemCount = this.readableNationalLanguageProblems
          ? new Set(this.readableNationalLanguageProblems.map(item => item.id)).size
          : new Set(this.readableNationalLanguagePlaylistProblems.map(item => item.id)).size;
      }
      this.totalProblemCount.emit({ problemCount });
    }

    this.columns = this.readableNationalLanguageProblems
      ? ['university', 'departments', 'year', 'outline', 'level', 'book', 'page', 'problemNumber', 'wordDownload']
      : ['sequentialId', 'university', 'departments', 'year', 'outline', 'level', 'book', 'page', 'problemNumber', 'wordDownload'];

    if (this.isShowBookmark) {
      this.columns.splice(0, 0, 'bookmark');
    }

    const aggregatedProblems = this.readableNationalLanguageProblems
      ? this.readableNationalLanguageProblems.reduce<ReadableNationalLanguageProblem[][]>((acc, it) => {
          const element = acc.find(problems => problems[0].id === it.id);
          if (element) acc[acc.indexOf(element)].push(it);
          else acc.push([it]);
          return acc;
        }, [])
      : [];

    const aggregatedPlaylistProblems = this.readableNationalLanguagePlaylistProblems
      ? this.readableNationalLanguagePlaylistProblems.reduce<ReadableNationalLanguagePlaylistProblem[][]>((acc, it) => {
          const element = acc.find(problems => problems[0].id === it.id);
          if (element) acc[acc.indexOf(element)].push(it);
          else acc.push([it]);
          return acc;
        }, [])
      : [];

    if (this.isForPrint) {
      this.dataSource.data = this.readableNationalLanguageProblems
        ? aggregatedProblems.map(readableProblem => this.mapDataForPrint(readableProblem))
        : [];
      return;
    }

    // お気に入り登録モダルで必要なデータ - お気に入りリスト一覧のデータ
    this.signedInUser$ = this.store.select(getSignedInUser).pipe(filter<User>(it => it != null));
    this.nationalLanguageBookmarkProblemLists$ = combineLatest([
      this.store.select(BookmarkProblemListSelectors.getBookmarkProblemLists).pipe(filter(it => it != null)),
      this.signedInUser$
    ]).pipe(
      map(([bookmarkLists, user]) => {
        return bookmarkLists.filter(bookmarkList => bookmarkList.subjectId === this.subjectId && bookmarkList.userId === user.id);
      })
    );

    // お気に入り登録モダルで必要なデータ - 各お気に入りリストに紐づく問題データ
    this.nationalLanguageBookmarkProblems$ = combineLatest([
      this.signedInUser$,
      this.store.select(BookmarkProblemsSelectors.getBookmarkProblems).pipe(filter(it => it != null))
    ]).pipe(
      map(([user, bookmarkProblems]) => {
        return bookmarkProblems.filter(
          bookmarkProblem => bookmarkProblem.subjectId === this.subjectId && bookmarkProblem.userId === user.id
        );
      })
    );

    // お気に入り登録モダルで必要なデータ - 未分類のお気に入りリストに紐づく問題データ
    this.store.dispatch(BookmarkActions.initializeFindBookmarkState());
    this.nationalLanguageUncategorizedBookmarkProblems$ = combineLatest([this.signedInUser$]).pipe(
      switchMap(([user]) => {
        this.store.dispatch(BookmarkActions.findBookmark({ userId: user.id }));
        return this.store.select(BookmarkSelectors.getBookmark).pipe(
          filter(bookmark => !!bookmark),
          map(bookmark => (bookmark.problems ?? []).filter(problem => problem.subjectId === this.subjectId))
        );
      })
    );
    this.uncategorizedBookmarkProblemCount$ = combineLatest([this.signedInUser$]).pipe(
      switchMap(([user]) => {
        this.store.dispatch(BookmarkActions.findBookmark({ userId: user.id }));
        return this.store.select(BookmarkSelectors.getBookmark).pipe(
          filter(bookmark => !!bookmark),
          map(bookmark => (bookmark.problems ?? []).filter(problem => problem.subjectId === this.subjectId).length)
        );
      })
    );

    combineLatest([
      this.nationalLanguageBookmarkProblemLists$,
      this.nationalLanguageBookmarkProblems$,
      this.nationalLanguageUncategorizedBookmarkProblems$,
      this.signedInUser$,
      this.uncategorizedBookmarkProblemCount$
    ])
      .pipe(take(1))
      .subscribe(([bookmarkProblemLists, bookmarkProblems, uncategorizedBookmarkProblems, user, uncategorizedProblemCount]) => {
        // 未分類のお気に入りリストに登録できる上限の数を取得
        const isUncategorizedProblemCountMaxExceeded = uncategorizedProblemCount >= UNCATEGORIZED_BOOKMARK_PROBLEM_LISTS_DEFAULT_MAX_COUNT;
        // お気に入りリストに登録できる上限の数を取得
        this.problemMaxCount = BookmarkProblemListsUtil.getProblemsMaxCount(user.schoolId);

        this.dataSource.data = this.readableNationalLanguageProblems
          ? aggregatedProblems.map(readableProblem =>
              this.mapData(
                readableProblem,
                bookmarkProblemLists,
                bookmarkProblems,
                uncategorizedBookmarkProblems,
                this.problemMaxCount,
                isUncategorizedProblemCountMaxExceeded
              )
            )
          : this.readableNationalLanguagePlaylistProblems
          ? aggregatedPlaylistProblems.map(readablePlaylistProblem => {
              const data = this.mapData(
                readablePlaylistProblem,
                bookmarkProblemLists,
                bookmarkProblems,
                uncategorizedBookmarkProblems,
                this.problemMaxCount,
                isUncategorizedProblemCountMaxExceeded
              );
              data.sequentialId = readablePlaylistProblem[0].sequentialId;
              if (readablePlaylistProblem[0].comment) data.comment = readablePlaylistProblem[0].comment;
              if (readablePlaylistProblem[0].pdfPath) {
                data.pdfPath = readablePlaylistProblem[0].pdfPath;
                data.pdfDownloading = false;
              }
              return data;
            })
          : [];

        setTimeout(() => {
          this.initialized.emit();
        }, 100);
      });
  }

  ngOnDestroy() {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.visibilitySub?.unsubscribe();
  }

  showPlayListProblemDetail(problem: ProblemData) {
    const url = RoutingPathResolver.resolvePlaylistProblemDetail(this.playListId, this.themeId, problem.id);
    this.store.dispatch(openWindow({ url }));
  }

  openProblemWord(problem: ProblemData) {
    const wordUrl = WordFileUtil.getPath(problem.id, this.isTrial);
    if (wordUrl === '') {
      Log.warn(this.LOG_SOURCE, `wordURL が設定されていません. problem id: `, problem.id);
      return;
    }
    Log.debug(this.LOG_SOURCE, `word をダウンロードして開きます. wordURL: `, wordUrl);

    const isEdge = /Edg/.test(navigator.userAgent);
    const doNotWordDownloadAlertShowAgain = localStorage.getItem('doNotWordDownloadAlertShowAgain');
    if (isEdge && doNotWordDownloadAlertShowAgain !== 'true') {
      this.wordAlertDialog(problem.id, wordUrl);
      return;
    }

    this.store.dispatch(redirectPage({ url: wordUrl }));

    const subjectId = problem.id.slice(6, 8);
    const selectedProblemWord = `${problem.id}.docx`;
    const appEvent: AppEvent = {
      type: 'select-problem-word',
      value: JSON.stringify({
        subjectId,
        selectedProblemWord
      })
    };
    setTimeout(() => this.store.dispatch(reportAppEvent({ event: appEvent })));
  }

  wordAlertDialog(problemId: string, wordUrl: string) {
    const config: MatDialogConfig = {
      width: WORD_DOWNLOAD_ALERT_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      autoFocus: false,
      data: { problemId, wordUrl },
      disableClose: true
    };
    this.dialog.open(WordDownloadAlertDialogComponent, config);
  }

  keywordHighlight(text: string): string {
    if (!this.keywords) return text;
    return this.highlightPipe.transform(text, ...this.keywords);
  }

  freewordHighlight(text: string): string {
    const html = text.replace(/<em>/g, '<em class="highlight">');
    return html;
  }

  onDrop(event: CdkDragDrop<ProblemData[]>) {
    this.isDragging = false;
    if (event.previousIndex === event.currentIndex) return;

    const data = this.dataSource.data.slice(); // コピーを取る
    moveItemInArray(data, event.previousIndex, event.currentIndex);
    this.dataSource.data = data;
    const problemIds = data.map(problem => problem.id);

    // 問題の並び替えAPIを呼び出す
    Log.debug(this.LOG_SOURCE, 'お気に入りリストの問題の並び替えを行います。', problemIds);

    this.dropped.emit({ bookmarkProblemListId: this.bookmarkProblemListId, problemIds });
  }

  onDragStarted(event: any) {
    this.isDragging = true;
  }

  onDragEnded(event: any): void {
    this.isDragging = false;
  }

  updateBookmarkProblemListPerProblem(event: {
    problemId: string;
    newBookmarkLists: BookmarkProblemListWithCheck[];
    newIsBookmarked: boolean;
  }) {
    // 各問題のお気に入り登録状態を更新
    const problem = this.dataSource.data.find(p => p.id === event.problemId);
    if (problem) {
      problem.bookmarkProblemLists = event.newBookmarkLists;
      problem.isBookmarked = event.newIsBookmarked;
    }
    this.dataSource.data = [...this.dataSource.data];
  }

  updateMaxCountFlag(event: { newBookmarkLists: BookmarkProblemListWithCheck[]; newIsBookmarked: boolean; problemId: string }) {
    // 問題数の上限フラグが更新された場合は、全ての問題のbookmarkProlemListsの該当リストIDのisProblemCountMaxExceededを更新する
    this.dataSource.data = this.dataSource.data.map(problem => {
      if (problem.id === event.problemId) {
        return {
          ...problem,
          bookmarkProblemLists: event.newBookmarkLists,
          isBookmarked: event.newIsBookmarked // お気に入り登録フラグ(isBookmarked)は問題IDに対して更新
        };
      } else {
        return {
          ...problem,
          bookmarkProblemLists: problem.bookmarkProblemLists.map(list => {
            const updated = event.newBookmarkLists.find(updatedList => updatedList.listId === list.listId);
            return updated ? { ...list, isProblemCountMaxExceeded: updated.isProblemCountMaxExceeded } : list;
          })
        };
      }
    });
  }

  updateNewBookmarkProblemLists(event: {
    newBookmarkLists: BookmarkProblemListWithCheck[];
    isBookmarked: boolean;
    newListIds: string[];
    problemId: string;
  }) {
    const newLists = event.newBookmarkLists.filter(list => event.newListIds.includes(list.listId));
    this.dataSource.data = this.dataSource.data.map(problem => {
      const updatedLists =
        problem.id === event.problemId
          ? event.newBookmarkLists
          : [...(problem.bookmarkProblemLists || []), ...newLists.map(list => ({ ...list, checked: false }))];

      const collator = new Intl.Collator('ja', { sensitivity: 'base' });
      const sortedLists = updatedLists.sort((a, b) => {
        // 未分類のリストのを先頭に、その他のリストは名前順
        if (a.listId === `uncategorized_${this.subjectId}`) return -1;
        if (b.listId === `uncategorized_${this.subjectId}`) return 1;
        return collator.compare(a.name, b.name);
      });

      return {
        ...problem,
        isBookmarked: problem.id === event.problemId ? event.isBookmarked : problem.isBookmarked,
        bookmarkProblemLists: sortedLists
      };
    });
  }

  private mapData(
    readableProblems: ReadableNationalLanguageProblem[],
    nationalLanguageBookmarkProblemLists: BookmarkProblemListTableData[],
    nationalLanguageBookmarkProblems: BookmarkProblemsCollection[],
    nationalLanguageUncategorizedProblems: BookmarkProblem[],
    problemMaxCount: number,
    isUncategorizedProblemCountMaxExceeded: boolean
  ): ProblemData {
    const readableProblem = readableProblems[0];
    const collator = new Intl.Collator('ja', { sensitivity: 'base' });
    const bookmarkProblemLists = nationalLanguageBookmarkProblemLists
      .map(list => {
        const isProblemCountMaxExceeded =
          list.id === `uncategorized_${this.subjectId}`
            ? isUncategorizedProblemCountMaxExceeded
            : list.problemCount >= problemMaxCount
            ? true
            : false;
        const isListBookmarked =
          nationalLanguageBookmarkProblems &&
          nationalLanguageBookmarkProblems.some(
            problem => list.id === problem.bookmarkProblemListId && problem.problemId === readableProblem.id
          );
        const isUncategorizedChecked =
          nationalLanguageUncategorizedProblems &&
          nationalLanguageUncategorizedProblems.some(problem => problem.problemId === readableProblem.id);

        return {
          listId: list.id,
          name: list.name,
          createdAt: list.createdAt,
          isProblemCountMaxExceeded,
          checked: list.id === `uncategorized_${this.subjectId}` ? isUncategorizedChecked : isListBookmarked
        };
      })
      .sort((a, b) => {
        // 未分類のリストのを先頭に、その他のリストは名前順
        if (a.listId === `uncategorized_${this.subjectId}`) return -1;
        if (b.listId === `uncategorized_${this.subjectId}`) return 1;
        return collator.compare(a.name, b.name);
      });

    const isBookmarked =
      this.bookmarkedAllFlag ||
      nationalLanguageUncategorizedProblems?.some(problem => problem.problemId === readableProblem.id) ||
      nationalLanguageBookmarkProblems?.some(
        nationalLanguageBookmarkProblem => nationalLanguageBookmarkProblem.problemId === readableProblem.id
      );

    const data = this.getProblemData(readableProblems, isBookmarked, bookmarkProblemLists);

    return data;
  }

  private mapDataForPrint(readableProblems: ReadableNationalLanguageProblem[]): ProblemData {
    return this.getProblemData(readableProblems);
  }

  private getOutlineData(
    readableProblems: ReadableNationalLanguageProblem[]
  ): { contents: string; descriptions: Description[]; isCommonOverview: boolean; highlight?: string } {
    const isThinking = readableProblems.reduce((acc, it) => (it.thinking !== '' ? it.thinking : acc), '');
    const categories = readableProblems.reduce((acc, it) => {
      if (acc.includes(it.categories)) return acc;
      if (acc !== '') {
        return acc + ', ' + it.categories;
      } else {
        return it.categories;
      }
    }, '');
    const unitContents = readableProblems
      .reduce((acc, it) => {
        if (it.unitContents) {
          it.unitContents.forEach(content => {
            if (acc.includes(content)) return;
            acc.push(content);
          });
        }
        return acc;
      }, [])
      .join(', ');
    const contents = unitContents ? isThinking + categories + ' / ' + unitContents : isThinking + categories;

    const descriptions = readableProblems
      .sort((a, b) => parseInt(a.problemOrder, 10) - parseInt(b.problemOrder, 10))
      .map<Description>(it => ({
        field: it.categories,
        problemOrder: it.displayProblemOrder,
        genre: it.unitGenreDescription ? `${it.unitGenre} / ${it.unitGenreDescription}` : it.unitGenre,
        author: it.author,
        title: it.title,
        titleComplement: it.titleComplement,
        characterCount: it.displayCharacterCount,
        overview: it.overview
      }));
    const isCommonOverview = readableProblems[0].isCommonOverview;
    const highlight = readableProblems[0].highlight ?? undefined;
    return {
      contents,
      descriptions,
      isCommonOverview,
      ...(highlight !== undefined && { highlight })
    };
  }

  private getProblemData(
    readableProblems: ReadableNationalLanguageProblem[],
    isBookmarked: boolean = undefined,
    bookmarkProblemLists: any[] = undefined
  ): ProblemData {
    const outline = this.getOutlineData(readableProblems);
    const data: ProblemData = {
      /** PlaylistProblem の場合は map 後に適切な値を設定 */
      sequentialId: '',

      id: readableProblems[0].id,
      university: readableProblems[0].university,
      departments: readableProblems[0].departments,
      year: readableProblems[0].year,
      outline,
      level: readableProblems[0].level,
      book: readableProblems[0].book,
      page: readableProblems.reduce<number>((acc, it, index) => (index === 0 || acc > it.page ? it.page : acc), 0),
      problemNumber: readableProblems[0].problemNumber,
      hasExternalData: readableProblems.reduce<boolean>((acc, it) => (it.hasExternalData === true ? true : acc), false),
      hasWordData: readableProblems.reduce<boolean>((acc, it) => (it.hasWordData === true ? true : acc), false)
    };
    if (isBookmarked !== undefined) data.isBookmarked = isBookmarked;
    if (bookmarkProblemLists !== undefined) data.bookmarkProblemLists = bookmarkProblemLists;

    return data;
  }

  private setUpBookmarkProblemLists() {
    this.store.dispatch(BookmarkProblemListActions.initializeFindMyBookmarkProblemListsState());
    this.store.dispatch(BookmarkProblemListActions.initializeFindSharedBookmarkProblemListsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        const findMyBookmarkProblemListParams: FindMyBookmarkProblemListParams = {
          userId: signedInUser.id,
          subjectIds: signedInUser.visibleSubjectIds
        };
        this.store.dispatch(BookmarkProblemListActions.findMyBookmarkProblemLists(findMyBookmarkProblemListParams));

        const findSharedBookmarkProblemListParams: FindSharedBookmarkProblemListParams = {
          schoolId: signedInUser.schoolId,
          userId: signedInUser.id
        };
        this.store.dispatch(BookmarkProblemListActions.findSharedBookmarkProblemLists(findSharedBookmarkProblemListParams));
      });
  }

  private setUpBookmarkProblems() {
    this.store.dispatch(BookmarkProblemsActions.initializeFindBookmarkProblemsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        const findBookmarkProblemsParams: FindBookmarkProblemsParams = {
          userId: signedInUser.id,
          subjectIds: signedInUser.visibleSubjectIds
        };
        this.store.dispatch(BookmarkProblemsActions.findBookmarkProblems(findBookmarkProblemsParams));
      });
  }
}
